/*******************************************************************************
 * Copyright     : Copyright (c)  2026 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for any purpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectOption,
  Card,
  CardContent,
  Button,
  ImportIcon,
  Control,
  Label,
  SelectOptionSearch,
  ControlHelper,
  ControlError,
  InfoIcon,
  CardHeader,
  DownloadIcon,
} from "@forge/common";

//@Models
import { IAvailableSoftwareVersion } from "../../../models/softwaredetails";

//@Utils
import { validateInput } from "../../../utils/utility";

//@Styles
import "./styles.scss";
import { LOCALE_CONST } from "../../../utils/constants";

interface SoftwareDetailsProps {
  availableSoftwareVersions?: IAvailableSoftwareVersion[];
  installedSoftwareVersion?: string;
  onDownload?: (version: string, releaseType: string) => void;
  onViewDetails?: (version: string, releaseType: string) => void;
  onSaveChanges?: (version: string, releaseType: string) => void;
  onVersionCardClick?: (version: string, releaseType: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isCreate?: boolean;
  showVersionError?: boolean;
  onVersionChange?: (version: string, releaseType: string) => void;
  showDownload?: boolean;
  isConnected?: boolean;
}

const SoftwareDetails: React.FC<SoftwareDetailsProps> = ({
  availableSoftwareVersions = [],
  installedSoftwareVersion = "",
  onDownload,
  onViewDetails,
  onSaveChanges,
  onVersionCardClick,
  isLoading = false,
  isDisabled = false,
  isCreate = false,
  showVersionError = false,
  onVersionChange,
  showDownload = false,
  isConnected = false,
}) => {
  const { t } = useTranslation();
  // Set initial selected version to the first installed version
  const [selectedVersion, setSelectedVersion] = useState("");
  const [isSaveChangesEnabled, setIsSaveChangesEnabled] = useState(false);
  const [versionSearchQuery, setVersionSearchQuery] = useState<string>("");
  const [showError, setShowError] = useState(false);

  const getInstalledVersionValue = (installedVersion: any): string => {
    if (typeof installedVersion === "string") {
      return installedVersion;
    }

    if (installedVersion && typeof installedVersion === "object") {
      return String(installedVersion.version || installedVersion.value || installedVersion.text || "");
    }

    return "";
  };

  const getInstalledReleaseTypeValue = (installedVersion: any): string => {
    if (installedVersion && typeof installedVersion === "object") {
      return String(installedVersion.releaseType || "");
    }

    return "";
  };

  // Get all installed versions for the dropdown
  const installedVersionOptions = availableSoftwareVersions
    .map((item) => ({
      key: item.key,
      version: getInstalledVersionValue(item.installedVersion),
      releaseType: getInstalledReleaseTypeValue(item.installedVersion),
    }))
    .filter((item) => item.version !== "");

// Remove duplicate versions
const uniqueInstalledVersionOptions = installedVersionOptions.filter(
  (option, index, self) =>
    self.findIndex(o => o.version === option.version) === index
);

// Filter versions based on search query
const filteredVersionOptions = versionSearchQuery
  ? uniqueInstalledVersionOptions.filter((installedVersion) =>
      installedVersion.version
        .toLowerCase()
        .includes(versionSearchQuery.toLowerCase()),
    )
  : uniqueInstalledVersionOptions;

  // Update selectedVersion when availableSoftwareVersions changes
  useEffect(() => {
    if (availableSoftwareVersions.length > 0) {
      const selectedItem = availableSoftwareVersions.find(
        (item) => getInstalledVersionValue(item.installedVersion) === selectedVersion,
      );

      const normalizedInstalledSoftwareVersion = getInstalledVersionValue(installedSoftwareVersion);
      if (!isCreate && !normalizedInstalledSoftwareVersion) {
        if (selectedVersion && selectedItem) {
          setIsSaveChangesEnabled(true);
          return;
        }

        setSelectedVersion("");
        setIsSaveChangesEnabled(false);
        return;
      }

      if (!isCreate && normalizedInstalledSoftwareVersion) {
        const installedItem = availableSoftwareVersions.find(
          (item) =>
            getInstalledVersionValue(item.installedVersion) === normalizedInstalledSoftwareVersion,
        );

        if (installedItem) {
          const installedReleaseType =
            getInstalledReleaseTypeValue(installedItem.installedVersion) || "";

          // Initialize from installed version only when selection is empty/invalid.
          // Keep user-selected version afterwards so Save Changes can enable.
          if (!selectedVersion || !selectedItem) {
            if (selectedVersion !== normalizedInstalledSoftwareVersion) {
              setSelectedVersion(normalizedInstalledSoftwareVersion);
              onVersionChange?.(normalizedInstalledSoftwareVersion, installedReleaseType);
            }
            setIsSaveChangesEnabled(false);
          } else if (selectedVersion === normalizedInstalledSoftwareVersion) {
            // User reverted back to the installed version — disable Save Changes
            setIsSaveChangesEnabled(false);
          }
          // else: user has a different version selected — leave isSaveChangesEnabled alone
          return;
        }
      }

      // Only disable save changes if selected version equals installed version
      if (selectedVersion && selectedItem && selectedVersion === normalizedInstalledSoftwareVersion) {
        setIsSaveChangesEnabled(false);
        return;
      }

      // In create mode, if the user has already selected a valid version, don't reset it
      if (isCreate && selectedVersion && selectedItem) {
        return;
      }

      let defaultVersion =
        getInstalledVersionValue(availableSoftwareVersions[0]?.installedVersion) || "";
      let defaultReleaseType =
        getInstalledReleaseTypeValue(availableSoftwareVersions[0]?.installedVersion) || "";

      if (!isCreate && installedSoftwareVersion && installedSoftwareVersion !== "") {
        const foundItem = availableSoftwareVersions.find(
          (item) => getInstalledVersionValue(item.installedVersion) === installedSoftwareVersion,
        );
        if (foundItem) {
          defaultVersion = installedSoftwareVersion;
          defaultReleaseType = getInstalledReleaseTypeValue(foundItem.installedVersion) || "";
        }
      }

      setSelectedVersion(defaultVersion);
      if (defaultVersion) {
        onVersionChange?.(defaultVersion, defaultReleaseType);
      }
    } else if (!isLoading) {
      setSelectedVersion("");
    }
    setIsSaveChangesEnabled(false);
  }, [availableSoftwareVersions, isCreate, installedSoftwareVersion, selectedVersion, isLoading]);

  // Get the available versions for the currently selected installed version
  const selectedSoftwareItem = availableSoftwareVersions.find(
    (item) => getInstalledVersionValue(item.installedVersion) === selectedVersion,
  );

  const availableVersionsForSelected =
    selectedSoftwareItem?.availableVersions || [];

  const handleVersionChange = (value: any) => {
    const extractVersionFromSelectPayload = (payload: any): string => {
      if (typeof payload === "string") {
        return payload;
      }

      if (payload && typeof payload === "object") {
        if (typeof payload.target?.value === "string") {
          return payload.target.value;
        }

        if (typeof payload.value === "string") {
          return payload.value;
        }

        if (typeof payload.id === "string") {
          const mappedOption = installedVersionOptions.find(
            (option) => option.key === payload.id,
          );
          return mappedOption?.version || payload.id;
        }

        if (typeof payload.label === "string") {
          return payload.label;
        }

        if (typeof payload.version === "string") {
          return payload.version;
        }
      }

      return "";
    };

    const newVersion = extractVersionFromSelectPayload(value);
    if (!newVersion) {
      return;
    }

    if (newVersion !== selectedVersion) {
      setIsSaveChangesEnabled(true);
    }
    setSelectedVersion(newVersion);
    setShowError(false); // Clear error when user selects a version
    if (onVersionChange) {
      const selectedItem = availableSoftwareVersions.find(
        (item) => getInstalledVersionValue(item.installedVersion) === newVersion,
      );
      const releaseType = getInstalledReleaseTypeValue(selectedItem?.installedVersion) || "";
      onVersionChange(newVersion, releaseType);
    }
  };

  // Update error state when prop changes
  useEffect(() => {
    setShowError(showVersionError);
  }, [showVersionError]);

  return (
    <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
      <span className="iot-basic-info-text p-5 block">
        {t(LOCALE_CONST.softwareDetails)}
      </span>
      <CardContent top="none">
        {isConnected && <Card className="info-card">
          <CardContent className="info-card-content">
            <InfoIcon strokeWidth={3} />
            <span className="info-card-text">
              {t(LOCALE_CONST.connectedDeviceInfoCard)}
            </span>
          </CardContent>
        </Card>}
        <Control className="mb-2 mt-2" spacingAbove spacingBelow>
          <Label className="font-normal" required requiredText="">
            {t(LOCALE_CONST.software_version_on_device)}
          </Label>
          <div className="flex items-center gap-4">
            <div style={{ width: "50%" }}>
              <Select
                key={`software-version-select-${selectedVersion || "empty"}`}
                id="software-version-select"
                defaultValue={selectedVersion}
                onChange={(value) => handleVersionChange(value)}
                skeleton={isLoading}
                disabled={isDisabled}
                required
                error={showError}
                placeholder={t(LOCALE_CONST.select_software_version)}
                search={
                  <SelectOptionSearch
                    label="Search"
                    onChange={(e) => {
                      if (validateInput(e.target.value)) {
                        setVersionSearchQuery(e.target.value);
                      }
                    }}
                    placeholder={t(LOCALE_CONST.search)}
                    value={versionSearchQuery}
                  />
                }
              >
                {filteredVersionOptions.map((installedVersion) => (
                  <SelectOption
                    id={installedVersion.key}
                    key={installedVersion.key}
                    value={installedVersion.version}
                    label={installedVersion.version}
                  />
                ))}
              </Select>
            </div>
            <Button
              variant="quiet-primary"
              onClick={() => {
                const releaseType =
                  getInstalledReleaseTypeValue(selectedSoftwareItem?.installedVersion) || "";
                onViewDetails?.(selectedVersion, releaseType);
              }}
              disabled={!selectedVersion}
              className="no-hover-bg"
            >
              {t(LOCALE_CONST.view_details)}
            </Button>
            {!isCreate && !isConnected && (
              <Button
                variant="secondary"
                onClick={() => {
                  const releaseType =
                    getInstalledReleaseTypeValue(selectedSoftwareItem?.installedVersion) || "";
                  onSaveChanges?.(selectedVersion, releaseType);
                }}
                disabled={!selectedVersion || !isSaveChangesEnabled}
              >
                {t(LOCALE_CONST.save_changes)}
              </Button>
            )}
          </div>
          {showError && (
            <ControlError showIcon>
              {t(LOCALE_CONST.requiredField)}
            </ControlError>
          )}
          <ControlHelper className="helper-text">
            {t(LOCALE_CONST.software_version_display_text)}
          </ControlHelper>
        </Control>

        {/* Available Versions Section */}
        {selectedVersion && (
          <div className="mb-4 mt-2">
            <span className="available_version-text block">
              {t(LOCALE_CONST.available_versions)}
            </span>
            {availableVersionsForSelected.length === 0 && (
              <span className="version-size block">
                {t(LOCALE_CONST.softwareUpToDate)}
              </span>
            )}
            {availableVersionsForSelected.slice(0, 1).map((item, index) => (
              <Card
                key={index}
                className="version-card"
                style={{ width: "61%" }}
              >
                <CardContent className="version-card-content">
                  <div className="flex-1">
                    <span
                      className="version-number"
                      onClick={() =>
                        onVersionCardClick?.(item.version, item.releaseType)
                      }
                      role="button"
                      tabIndex={0}
                      aria-label={`View details for ${item.version}`}
                    >
                      {item.version}
                    </span>
                    <span className="version-size block">{item.size}</span>
                  </div>
                  {showDownload && (
                    <Button
                      icon={<ImportIcon strokeWidth={3} />}
                      onClick={() =>
                        onDownload?.(item.version, item.releaseType)
                      }
                      variant="quiet-primary"
                      className="no-hover-bg"
                    >
                      {t(LOCALE_CONST.download)}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SoftwareDetails;
